import React, { useEffect, useState } from 'react';
import {
 collection,
 query,
 where,
 onSnapshot,
 doc,
 updateDoc,
 Timestamp,
 addDoc,
 getDocs,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { User, VIPAppointment } from '../../types';
import { toast } from '@/lib/toast';

interface VIPUser extends User {
 vipSince?: Timestamp;
 vipExpiresAt?: Timestamp;
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

const VIPManagement: React.FC<Props> = ({ selectedFranchiseId, selectedBranchId }) => {
 const [vipUsers, setVipUsers] = useState<VIPUser[]>([]);
 const [pendingRequests, setPendingRequests] = useState<VIPAppointment[]>([]);
 const [loading, setLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState('');
 const [showAssignModal, setShowAssignModal] = useState(false);
 const [searchResults, setSearchResults] = useState<User[]>([]);
 const [assigningUser, setAssigningUser] = useState<User | null>(null);
 const [expiryDays, setExpiryDays] = useState(365);

 useEffect(() => {
 // Subscribe to VIP users
 let vipUsersQuery = query(collection(db, 'users'), where('isVIP', '==', true));

 if (selectedFranchiseId !== 'all') {
 vipUsersQuery = query(vipUsersQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 vipUsersQuery = query(vipUsersQuery, where('branchId', '==', selectedBranchId));
 }

 const unsubscribeVIP = onSnapshot(vipUsersQuery, (snapshot) => {
 const users = snapshot.docs.map((doc) => ({
 id: doc.id,
 ...doc.data(),
 })) as VIPUser[];

 setVipUsers(users);
 setLoading(false);
 });

 // Subscribe to pending VIP appointment requests (citas pendientes de confirmación)
 let pendingQuery = query(
 collection(db, 'appointments'),
 where('status', '==', 'pending')
 );

 if (selectedFranchiseId !== 'all') {
 pendingQuery = query(pendingQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 pendingQuery = query(pendingQuery, where('branchId', '==', selectedBranchId));
 }

 const unsubscribePending = onSnapshot(pendingQuery, (snapshot) => {
 const requests = snapshot.docs.map((doc) => ({
 appointmentId: doc.id,
 ...doc.data(),
 })) as VIPAppointment[];

 setPendingRequests(requests);
 });

 return () => {
 unsubscribeVIP();
 unsubscribePending();
 };
 }, [selectedFranchiseId, selectedBranchId]);

 const handleSearchUsers = async (searchQuery: string) => {
 if (!searchQuery || searchQuery.trim() === '') {
 setSearchResults([]);
 return;
 }

 try {
 let usersQuery = query(collection(db, 'users'));

 // Apply filters based on selected franchise/branch
 if (selectedFranchiseId !== 'all') {
 usersQuery = query(usersQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 usersQuery = query(usersQuery, where('branchId', '==', selectedBranchId));
 }

 const snapshot = await getDocs(usersQuery);

 const results = snapshot.docs
 .map((doc) => ({
 id: doc.id,
 ...doc.data(),
 }))
 .filter((user: any) => {
 const matchesSearch =
 user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
 user.email?.toLowerCase().includes(searchQuery.toLowerCase());
 const notVIP = !user.isVIP;
 return matchesSearch && notVIP;
 }) as User[];

 setSearchResults(results.slice(0, 10));
 } catch (error) {
 console.error('Error searching users:', error);
 }
 };

 const handleAssignVIP = async (user: User) => {
 setAssigningUser(user);
 };

 const confirmAssignVIP = async () => {
 if (!assigningUser) return;

 try {
 const expiresAt = new Date();
 expiresAt.setDate(expiresAt.getDate() + expiryDays);

 const userRef = doc(db, 'users', assigningUser.id);
 await updateDoc(userRef, {
 isVIP: true,
 vipSince: Timestamp.now(),
 vipExpiresAt: Timestamp.fromDate(expiresAt),
 updatedAt: Timestamp.now(),
 });

 // Log the change in an audit collection
 await addDoc(collection(db, 'vip_audit_log'), {
 userId: assigningUser.id,
 action: 'assigned',
 expiresAt: Timestamp.fromDate(expiresAt),
 performedAt: Timestamp.now(),
 });

 setAssigningUser(null);
 setShowAssignModal(false);
 setSearchTerm('');
 setSearchResults([]);
 toast.success('VIP status assigned successfully');
 } catch (error) {
 console.error('Error assigning VIP status:', error);
 toast.error('Failed to assign VIP status. Please try again.');
 }
 };

 const handleRevokeVIP = async (userId: string) => {
 try {
 const userRef = doc(db, 'users', userId);
 await updateDoc(userRef, {
 isVIP: false,
 vipSince: null,
 vipExpiresAt: null,
 updatedAt: Timestamp.now(),
 });

 // Log the change
 await addDoc(collection(db, 'vip_audit_log'), {
 userId: userId,
 action: 'revoked',
 performedAt: Timestamp.now(),
 });
 toast.success('VIP status revoked successfully');
 } catch (error) {
 console.error('Error revoking VIP status:', error);
 toast.error('Failed to revoke VIP status. Please try again.');
 }
 };

 const handleExtendVIP = async (userId: string, currentExpiry: Timestamp) => {
 // TODO: Replace with modal input
 const additionalDays = prompt('How many days to extend VIP status?', '90');
 if (!additionalDays) return;

 const days = parseInt(additionalDays);
 if (isNaN(days) || days <= 0) {
 toast.error('Please enter a valid number of days');
 return;
 }

 try {
 const newExpiry = currentExpiry.toDate();
 newExpiry.setDate(newExpiry.getDate() + days);

 const userRef = doc(db, 'users', userId);
 await updateDoc(userRef, {
 vipExpiresAt: Timestamp.fromDate(newExpiry),
 updatedAt: Timestamp.now(),
 });

 // Log the change
 await addDoc(collection(db, 'vip_audit_log'), {
 userId: userId,
 action: 'extended',
 extensionDays: days,
 newExpiresAt: Timestamp.fromDate(newExpiry),
 performedAt: Timestamp.now(),
 });
 toast.success(`VIP status extended by ${days} days`);
 } catch (error) {
 console.error('Error extending VIP status:', error);
 toast.error('Failed to extend VIP status. Please try again.');
 }
 };

 const formatDate = (timestamp: Timestamp | undefined) => {
 if (!timestamp) return 'N/A';
 return timestamp.toDate().toLocaleDateString();
 };

 const getDaysRemaining = (expiresAt: Timestamp | undefined) => {
 if (!expiresAt) return null;
 const now = new Date();
 const expiry = expiresAt.toDate();
 const diff = expiry.getTime() - now.getTime();
 const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
 return days;
 };

 const filteredVIPUsers = vipUsers.filter(
 (user) =>
 user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 user.email?.toLowerCase().includes(searchTerm.toLowerCase())
 );

 if (loading) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto"></div>
 <p className="mt-4 text-ink-muted">Loading VIP management...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="p-6 space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h2 className="text-2xl font-bold text-ink">VIP Management</h2>
 <p className="text-sm text-ink-muted mt-1 text-ink-muted">
 Manage VIP users and appointment requests
 </p>
 </div>
 <button
 onClick={() => setShowAssignModal(true)}
 className="px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors"
 >
 + Assign VIP Status
 </button>
 </div>

 {/* Pending Requests */}
 {pendingRequests.length > 0 && (
 <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
 <h3 className="text-lg font-semibold text-accent mb-3">
 Pending VIP Appointment Requests ({pendingRequests.length})
 </h3>
 <div className="space-y-2">
 {pendingRequests.map((request) => (
 <div
 key={request.appointmentId}
 className="bg-surface rounded-lg p-3 flex items-center justify-between"
 >
 <div>
 <p className="font-medium text-ink">{request.userName}</p>
 <p className="text-sm text-ink-muted">
 Requested: {formatDate(request.createdAt)} | Service: {request.serviceName}
 </p>
 </div>
 <button className="px-3 py-1 text-sm font-medium text-info bg-info/10 rounded hover:bg-info/20">
 View Details
 </button>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Search VIP Users */}
 <div>
 <input
 type="text"
 placeholder="Search VIP users..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
 />
 </div>

 {/* VIP Users List */}
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-ink">
 VIP Users ({filteredVIPUsers.length})
 </h3>
 {filteredVIPUsers.map((user) => {
 const daysRemaining = getDaysRemaining(user.vipExpiresAt);
 const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30;
 const isExpired = daysRemaining !== null && daysRemaining < 0;

 return (
 <div
 key={user.id}
 className="bg-white border border-hairline rounded-lg p-6 hover:shadow-md transition-shadow bg-surface-2 border-hairline"
 >
 <div className="flex items-start justify-between">
 <div className="flex items-start space-x-4 flex-1">
 {user.photoURL ? (
 <img
 src={user.photoURL}
 alt={user.displayName}
 className="w-16 h-16 rounded-full border-2 border-accent/30"
 />
 ) : (
 <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center border-2 border-accent/30">
 <span className="text-2xl">⭐</span>
 </div>
 )}
 <div className="flex-1">
 <div className="flex items-center space-x-2">
 <h3 className="text-lg font-semibold text-ink">{user.displayName}</h3>
 {isExpired ? (
 <span className="px-2 py-1 text-xs font-medium rounded-full bg-danger/10 text-danger">
 Expired
 </span>
 ) : isExpiringSoon ? (
 <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning/10 text-warning">
 Expiring Soon
 </span>
 ) : (
 <span className="px-2 py-1 text-xs font-medium rounded-full bg-success/10 text-success">
 Active
 </span>
 )}
 </div>
 <p className="text-sm text-ink-muted mt-1 text-ink-muted">{user.email}</p>
 <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
 <div>
 <span className="text-ink-muted">VIP Since:</span>
 <p className="font-semibold text-ink">{formatDate(user.vipSince)}</p>
 </div>
 <div>
 <span className="text-ink-muted">Expires:</span>
 <p className="font-semibold text-ink">
 {formatDate(user.vipExpiresAt)}
 </p>
 </div>
 {daysRemaining !== null && (
 <div>
 <span className="text-ink-muted">Days Remaining:</span>
 <p
 className={`font-semibold ${
 isExpired
 ? 'text-danger'
 : isExpiringSoon
 ? 'text-warning'
 : 'text-success'
 }`}
 >
 {daysRemaining} days
 </p>
 </div>
 )}
 <div>
 <span className="text-ink-muted">Loyalty Points:</span>
 <p className="font-semibold text-ink">{user.queuePoints}</p>
 </div>
 </div>
 </div>
 </div>

 <div className="flex flex-col space-y-2">
 <button
 onClick={() =>
 user.vipExpiresAt && handleExtendVIP(user.id, user.vipExpiresAt)
 }
 className="px-4 py-2 text-sm font-medium text-info bg-info/10 rounded-lg hover:bg-info/20 transition-colors"
 >
 Extend
 </button>
 <button
 onClick={() => handleRevokeVIP(user.id)}
 className="px-4 py-2 text-sm font-medium text-danger bg-danger/10 rounded-lg hover:bg-danger/20 transition-colors"
 >
 Revoke
 </button>
 </div>
 </div>
 </div>
 );
 })}

 {filteredVIPUsers.length === 0 && (
 <div className="text-center py-12 text-ink-subtle">
 <p>No VIP users found</p>
 </div>
 )}
 </div>

 {/* Assign VIP Modal */}
 {showAssignModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-surface-2">
 <div className="p-6 border-b border-hairline">
 <h3 className="text-xl font-semibold text-ink">Assign VIP Status</h3>
 </div>
 <div className="p-6 space-y-4">
 {!assigningUser ? (
 <>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Search Users
 </label>
 <input
 type="text"
 placeholder="Search by name or email..."
 value={searchTerm}
 onChange={(e) => {
 setSearchTerm(e.target.value);
 handleSearchUsers(e.target.value);
 }}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
 />
 </div>

 {searchResults.length > 0 && (
 <div className="border border-hairline rounded-lg divide-y divide-hairline dark:divide-hairline">
 {searchResults.map((user) => (
 <div
 key={user.id}
 className="p-3 hover:bg-surface-3 flex items-center justify-between"
 >
 <div>
 <p className="font-medium text-ink">{user.displayName}</p>
 <p className="text-sm text-ink-muted">{user.email}</p>
 </div>
 <button
 onClick={() => handleAssignVIP(user)}
 className="px-3 py-1 text-sm font-medium text-white bg-info rounded hover:bg-info/90"
 >
 Select
 </button>
 </div>
 ))}
 </div>
 )}
 </>
 ) : (
 <>
 <div className="bg-info/10 rounded-lg p-4 bg-info/10">
 <p className="text-sm text-ink-muted mb-2 text-ink-muted">Assigning VIP status to:</p>
 <p className="font-semibold text-ink">{assigningUser.displayName}</p>
 <p className="text-sm text-ink-muted">{assigningUser.email}</p>
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Expiry Duration (days)
 </label>
 <input
 type="number"
 value={expiryDays}
 onChange={(e) => setExpiryDays(parseInt(e.target.value))}
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">
 Expires on:{' '}
 {new Date(
 Date.now() + expiryDays * 24 * 60 * 60 * 1000
 ).toLocaleDateString()}
 </p>
 </div>
 </>
 )}
 </div>
 <div className="p-6 border-t border-hairline flex justify-end space-x-3 border-hairline">
 <button
 onClick={() => {
 setShowAssignModal(false);
 setAssigningUser(null);
 setSearchTerm('');
 setSearchResults([]);
 }}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors bg-surface-2 text-ink-muted"
 >
 Cancel
 </button>
 {assigningUser && (
 <button
 onClick={confirmAssignVIP}
 className="px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors"
 >
 Assign VIP Status
 </button>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default VIPManagement;
